import type {FileRecordType} from '@packages/systems/core/records/FileRecord';
import {isMintId, makeMintId} from '@packages/systems/core/utils/RecordUtils';
import {FileRecord} from '@packages/systems/core/records/FileRecord';
import {ActionCreator} from '@packages/utilities/create-action';
import {
  FILE_HASHING_FINISHED,
  VIDEO_SELECTED_FOR_UPLOAD,
  ASSET_UPLOAD_PROGRESSED,
} from '@packages/systems/designer/actions';
import {reifyFileBlob} from '@packages/shared/helpers/FileHelper';
import {findExistingIdenticalAsset} from '@packages/shared/utils/AssetUtils';
import {AssetState} from '@packages/systems/designer/assets';

type singleAssetPayload = {
  asset: FileRecord;
};

export type singleAssetActionCreator = ActionCreator<
  typeof VIDEO_SELECTED_FOR_UPLOAD,
  singleAssetPayload
>;

export const uploadSiteVideo = ({
  blob,
  onDone,
  onFail,
  onPreview,
  onSign,
  onProgress,
  flux,
  initialActionCreator,
  getSiteOrDbIdParams,
  isSigned,
  isDone,
  getDoneAsset,
  isFailed,
}: {
  blob: File;
  onDone?: (arg1: FileRecordType) => any;
  onFail?: () => any;
  onPreview?: (arg1: FileRecordType) => any;
  onSign?: (arg1: FileRecordType) => any;
  onProgress?: (arg1: FileRecordType) => any;
  flux: any;
  initialActionCreator: singleAssetActionCreator;
  getSiteOrDbIdParams: () =>
    | {
        databaseId: string;
      }
    | {
        siteId: string;
      };
  isSigned: (arg1: {
    type: string;
    asset: FileRecordType;
    mintId: string;
    fileHash?: string;
    siteId?: string;
    databaseId?: string;
  }) => boolean;
  isDone: (arg1: Record<any, any>, arg2: string, arg3: string) => boolean;
  getDoneAsset: (
    arg1: string,
    assetStore: AssetState
  ) => FileRecordType | null | undefined;
  isFailed: (arg1: Record<any, any>, arg2: string, arg3: string) => boolean;
}) => {
  const mintId = makeMintId();

  // @ts-expect-error - TS2345 - Argument of type '{ databaseId: string; window: Window & typeof globalThis; date: string; id: string; blob: File; } | { siteId: string; window: Window & typeof globalThis; date: string; id: string; blob: File; }' is not assignable to parameter of type '{ window: any; date: any; id: any; blob: any; siteId: any; databaseId: any; }'.
  const mintAsset = reifyFileBlob({
    window,
    date: new Date().toISOString(),
    id: mintId,
    blob,
    ...getSiteOrDbIdParams(),
  });

  // @ts-expect-error - TS7034 - Variable 'realId' implicitly has type 'any' in some locations where its type cannot be determined.
  let realId;
  // @ts-expect-error - TS7034 - Variable 'fileHash' implicitly has type 'any' in some locations where its type cannot be determined.
  let fileHash;
  // @ts-expect-error - TS7034 - Variable 'siteId' implicitly has type 'any' in some locations where its type cannot be determined.
  let siteId;
  // @ts-expect-error - TS7034 - Variable 'databaseId' implicitly has type 'any' in some locations where its type cannot be determined.
  let databaseId;

  // @ts-expect-error - TS7006 - Parameter 'action' implicitly has an 'any' type.
  const listener = flux.dispatcher.register((action) => {
    if (
      action.type === FILE_HASHING_FINISHED &&
      action.payload.meta.asset._id === mintId
    ) {
      // When the action `FILE_HASHING_FINISHED` is fired, we need to check if the asset that
      // was just hashed is already stored in the assets. If the asset is already stored in
      // the assets, we can use its upload progress status by assigning its ID to `realId`.
      const existingAsset = findExistingIdenticalAsset(
        action.payload.meta.asset.set('fileHash', action.payload.result)
      )(action.state.AssetStore.assets);

      // If the existing asset has still a mint ID, we can't use it so we need to wait
      // until the asset has been signed to get the real ID.
      if (existingAsset && !isMintId(existingAsset._id)) {
        realId = existingAsset._id;
      }

      fileHash = action.payload.result;
      siteId = action.payload.meta.asset.siteId;
      databaseId = action.payload.meta.asset.databaseId;
    }
    if (
      isSigned({
        type: action.type,
        asset: action.meta,
        mintId,
        // @ts-expect-error - TS7005 - Variable 'fileHash' implicitly has an 'any' type.
        fileHash,
        // @ts-expect-error - TS7005 - Variable 'siteId' implicitly has an 'any' type.
        siteId,
        // @ts-expect-error - TS7005 - Variable 'databaseId' implicitly has an 'any' type.
        databaseId,
      })
    ) {
      realId = action.response.get('_id');
      onSign && onSign(flux.getStoreState('AssetStore').assets.get(realId));
      // @ts-expect-error - TS7005 - Variable 'realId' implicitly has an 'any' type.
    } else if (isDone(action, mintId, realId)) {
      flux.dispatcher.unregister(listener);

      if (onDone) {
        const doneAsset = getDoneAsset(
          // @ts-expect-error TS7005 - Variable realId implicitly has an any type.
          realId,
          flux.getStoreState('AssetStore')
        );
        if (doneAsset) {
          onDone(doneAsset);
        } else {
          // According to flow types, the asset can be missing. In that
          // case we need to consider the upload to be failed.
          onFail && onFail();
        }
      }
      // @ts-expect-error - TS7005 - Variable 'realId' implicitly has an 'any' type.
    } else if (isFailed(action, mintId, realId)) {
      flux.dispatcher.unregister(listener);
      onFail && onFail();
    } else if (
      onProgress &&
      action.type === ASSET_UPLOAD_PROGRESSED &&
      // @ts-expect-error - TS7005 - Variable 'realId' implicitly has an 'any' type.
      action.meta === realId
    ) {
      // @ts-expect-error - TS7005 - Variable 'realId' implicitly has an 'any' type.
      onProgress(flux.getStoreState('AssetStore').assets.get(realId));
    }
  });

  flux.dispatch(
    initialActionCreator({
      asset: mintAsset,
    })
  );

  onPreview && onPreview(mintAsset);
};
